import { useEffect, useRef } from 'react'

export interface UseHotkeyOptions {
  enabled?: boolean
  /** Also fire while typing in an input, textarea or contenteditable (e.g. mod+k). */
  allowInInputs?: boolean
}

const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

// `mod` is ⌘ on macOS and Ctrl elsewhere; letters are case-insensitive.
export function matchesHotkey(e: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>, combo: string, mac = isMac()): boolean {
  const parts = combo.toLowerCase().split('+')
  const key = parts.pop() ?? ''
  const want = { mod: parts.includes('mod'), alt: parts.includes('alt'), shift: parts.includes('shift') }
  const modPressed = mac ? e.metaKey : e.ctrlKey
  if (want.mod !== modPressed) return false
  if (want.alt !== e.altKey) return false
  // "?" and "/" differ only by shift on most layouts; for symbols compare the produced key and ignore shift.
  const symbol = key.length === 1 && !/[a-z0-9]/.test(key)
  if (!symbol && want.shift !== e.shiftKey) return false
  return e.key.toLowerCase() === key
}

function typingInto(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

export function useHotkey(combo: string, handler: (e: KeyboardEvent) => void, { enabled = true, allowInInputs = false }: UseHotkeyOptions = {}) {
  const ref = useRef(handler)
  ref.current = handler
  useEffect(() => {
    if (!enabled) return
    const onKey = (e: KeyboardEvent) => {
      if (!allowInInputs && typingInto(e.target)) return
      if (!matchesHotkey(e, combo)) return
      e.preventDefault()
      ref.current(e)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [combo, enabled, allowInInputs])
}

export const modKeyLabel = () => (isMac() ? '⌘' : 'Ctrl')
